import { Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { dailyTime } from "@/api/auth";
import { formatDurationHms } from "@/lib/formatDate";

const RECONCILE_MS = 5 * 60 * 1000; // refetch server total every 5 min

export function WorkTimer() {
  // baseSeconds = closed-session seconds accumulated today (IST).
  // activeSince = login_at of the currently open session, IF it started
  // today IST. Cross-midnight session shows base=0, activeSince=null on the
  // new day (intentional; see plan's edge-case notes).
  const [baseSeconds, setBaseSeconds] = useState<number | null>(null);
  const [activeSince, setActiveSince] = useState<Date | null>(null);
  const [now, setNow] = useState<Date>(new Date());
  const [error, setError] = useState<boolean>(false);

  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    async function refetch() {
      try {
        const r = await dailyTime();
        if (!mountedRef.current) return;
        setBaseSeconds(r.accumulated_seconds);
        setActiveSince(
          r.active_session_started_at ? new Date(r.active_session_started_at) : null,
        );
        setError(false);
      } catch {
        if (mountedRef.current) setError(true);
      }
    }
    refetch();
    const t = setInterval(refetch, RECONCILE_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (baseSeconds === null) {
    return null; // don't flash "00:00:00" before we know
  }

  const liveExtra = activeSince
    ? Math.max(0, Math.floor((now.getTime() - activeSince.getTime()) / 1000))
    : 0;
  const displayed = baseSeconds + liveExtra;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-mono text-slate-700 dark:text-slate-200"
      title={
        error
          ? "Working time today (IST) – server unreachable, showing last known"
          : "Working time today (IST)"
      }
      aria-label="Working time today"
    >
      <Clock size={12} />
      {formatDurationHms(displayed)}
    </span>
  );
}
